import axios from 'axios';
import { authService } from './auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const apiClient = axios.create({
  baseURL: API
});

apiClient.interceptors.request.use((config) => {
  const token = authService.getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Empresa activa (selector "Empresa Actual") → el backend aísla las vistas a esa empresa/tenant.
  try {
    const active = localStorage.getItem('current_company_id');
    if (active) config.headers['X-Active-Company'] = active;
  } catch (e) { /* noop */ }
  return config;
});

// Reintentos con backoff para GET ante cold-start / errores transitorios de red.
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      authService.logout();
      window.location.href = '/login';
      return Promise.reject(error);
    }
    const config = error.config;
    const method = (config?.method || 'get').toLowerCase();
    const status = error.response?.status;
    const retriable = !error.response || [502, 503, 504].includes(status);
    if (config && method === 'get' && retriable) {
      config.__retryCount = config.__retryCount || 0;
      if (config.__retryCount < 3) {
        config.__retryCount += 1;
        await new Promise((r) => setTimeout(r, 1200 * config.__retryCount));
        return apiClient(config);
      }
    }
    return Promise.reject(error);
  }
);

export const api = {
  // Dashboard
  getDashboardStats: () => apiClient.get('/stats/dashboard'),

  // Users
  getUsers: (params) => apiClient.get('/users', { params }),
  getUser: (id) => apiClient.get(`/users/${id}`),
  createUser: (data) => apiClient.post('/auth/register', data),
  updateUser: (id, data) => apiClient.put(`/users/${id}`, data),
  deleteUser: (id) => apiClient.delete(`/users/${id}`),

  // Companies
  getCompanies: (includeInactive) => apiClient.get('/companies', { params: includeInactive ? { include_inactive: true } : {} }),
  createCompany: (data) => apiClient.post('/companies', data),
  getTenants: () => apiClient.get('/tenants'),
  provisionTenant: (data) => apiClient.post('/tenants/provision', data),
  getModulesCatalog: () => apiClient.get('/license/modules-catalog'),
  updateTenantModules: (id, modules) => apiClient.patch(`/tenants/${id}/modules`, { modules }),
  updateCompanyModules: (id, modules) => apiClient.patch(`/companies/${id}/modules`, { modules }),
  updateTenantTerms: (id, data) => apiClient.patch(`/tenants/${id}/terms`, data),
  getTenantContract: (id) => apiClient.get(`/tenants/${id}/contract.pdf`, { responseType: 'blob' }),
  getPublicBrand: (slug) => apiClient.get(`/public/brand/${slug}`),
  getCompanyClauses: (companyId) => apiClient.get('/policies/company-clauses', { params: companyId ? { company_id: companyId } : {} }),
  setCompanyClauses: (companyId, clauses) => apiClient.put('/policies/company-clauses', { company_id: companyId, clauses }),
  getMasterAgenda: () => apiClient.get('/master/agenda'),
  createMasterAgenda: (data) => apiClient.post('/master/agenda', data),
  deleteMasterAgenda: (id) => apiClient.delete(`/master/agenda/${id}`),
  getAvailability: (params) => apiClient.get('/inventory/availability', { params }),
  createTransferRequest: (data) => apiClient.post('/inventory/transfer-requests', data),
  getTransferRequests: () => apiClient.get('/inventory/transfer-requests'),
  approveTransferRequest: (id) => apiClient.post(`/inventory/transfer-requests/${id}/approve`),
  rejectTransferRequest: (id, reason) => apiClient.post(`/inventory/transfer-requests/${id}/reject`, { reason }),
  addSucursal: (tenantId, data) => apiClient.post(`/tenants/${tenantId}/sucursales`, data),
  linkExistingTenant: (data) => apiClient.post('/tenants/link-existing', data),
  updateCompany: (id, data) => apiClient.put(`/companies/${id}`, data),
  deleteCompany: (id) => apiClient.delete(`/companies/${id}`),

  // Products
  getProducts: (params) => apiClient.get('/products', { params }),
  createProduct: (data) => apiClient.post('/products', data),
  updateProduct: (id, data) => apiClient.put(`/products/${id}`, data),
  deleteProduct: (id) => apiClient.delete(`/products/${id}`),
  getPriceB: () => apiClient.get('/settings/price-b'),
  setPriceB: (payload) => apiClient.put('/settings/price-b', typeof payload === 'object' ? payload : { discount_percent: payload }),
  getPriceBHistory: () => apiClient.get('/settings/price-b/history'),
  downloadProductTemplate: () => apiClient.get('/products/template/download', { responseType: 'blob' }),
  bulkUploadProductsExcel: (formData, params) => apiClient.post('/products/bulk/excel', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    params: params || {}
  }),

  // Form Templates (Master)
  getTemplates: (target) => apiClient.get('/templates', { params: target ? { target } : {} }),
  createTemplate: (data) => apiClient.post('/templates', data),
  updateTemplate: (id, data) => apiClient.put(`/templates/${id}`, data),
  deleteTemplate: (id) => apiClient.delete(`/templates/${id}`),

  // Clients
  getClients: (params) => apiClient.get('/clients', { params }),
  createClient: (data) => apiClient.post('/clients', data),
  updateClient: (id, data) => apiClient.put(`/clients/${id}`, data),
  getClient: (id) => apiClient.get(`/clients/${id}`),
  requestClientCredit: (id, amount) => apiClient.post(`/clients/${id}/credit/request`, { credit_limit_requested: amount }),
  approveClientCredit: (id, approvedLimit) => apiClient.post(`/clients/${id}/credit/approve`, { approved_limit: approvedLimit }),
  rejectClientCredit: (id) => apiClient.post(`/clients/${id}/credit/reject`),

  // Categories (inventario)
  getCategories: (params) => apiClient.get('/categories', { params }),
  createCategory: (data) => apiClient.post('/categories', data),
  updateCategory: (id, data) => apiClient.put(`/categories/${id}`, data),
  deleteCategory: (id) => apiClient.delete(`/categories/${id}`),

  // Almacenes / localidades
  getWarehouses: (params) => apiClient.get('/warehouses', { params }),
  createWarehouse: (data) => apiClient.post('/warehouses', data),
  updateWarehouse: (id, data) => apiClient.put(`/warehouses/${id}`, data),
  deleteWarehouse: (id) => apiClient.delete(`/warehouses/${id}`),

  // Acciones masivas de inventario
  bulkDeleteProducts: (data) => apiClient.post('/products/bulk/delete', data),
  bulkMoveProducts: (data) => apiClient.post('/products/bulk/move', data),

  // Sales
  getSales: (params) => apiClient.get('/sales', { params }),
  createSale: (data) => apiClient.post('/sales', data),
  updateSale: (id, data) => apiClient.put(`/sales/${id}`, data),
  getSale: (id) => apiClient.get(`/sales/${id}`),
  convertBudget: (id, targetType) => apiClient.post(`/sales/${id}/convert`, null, { params: { target_type: targetType } }),

  // Roles
  getRoles: () => apiClient.get('/roles'),
  createRole: (data) => apiClient.post('/roles', data),
  updateRole: (id, data) => apiClient.put(`/roles/${id}`, data),
  getPermissions: () => apiClient.get('/permissions'),

  // Notifications
  sendNotification: (data) => apiClient.post('/notifications/send', data),

  // Reports
  exportSalesReport: (sellerId, companyId) => {
    return apiClient.get('/reports/sales', {
      params: { format: 'excel', seller_id: sellerId, company_id: companyId || undefined },
      responseType: 'blob'
    });
  },
  exportProductsReport: (companyId) => {
    return apiClient.get('/reports/products', {
      params: { format: 'excel', company_id: companyId || undefined },
      responseType: 'blob'
    });
  },
  exportClientsReport: () => {
    return apiClient.get('/reports/clients', {
      params: { format: 'excel' },
      responseType: 'blob'
    });
  },

  // Auth extras
  getMe: () => apiClient.get('/auth/me'),
  changePassword: (data) => apiClient.post('/auth/change-password', data),

  // Sales extras
  processSale: (id) => apiClient.post(`/sales/${id}/process`),
  updateDispatchInfo: (id, data) => apiClient.patch(`/sales/${id}/dispatch`, data),
  getSaleTicket: (id) => apiClient.get(`/sales/${id}/ticket`, { responseType: 'blob' }),
  getDispatchTicket: (id) => apiClient.get(`/sales/${id}/dispatch-ticket`, { responseType: 'blob' }),
  registerPayment: (id, amount, method = {}) => apiClient.post(`/sales/${id}/payment`, null, { params: { amount, ...method } }),
  getPaymentReceipt: (paymentId) => apiClient.get(`/payments/${paymentId}/receipt`, { responseType: 'blob' }),

  // Finance
  getFinanceSummary: (companyId) => apiClient.get('/finance/summary', { params: companyId ? { company_id: companyId } : {} }),
  getClientFinance: (clientId) => apiClient.get(`/finance/client/${clientId}`),
  createFinancialTx: (data) => apiClient.post('/finance/transactions', data),
  getFinancialTxs: (params) => apiClient.get('/finance/transactions', { params }),
  deleteFinancialTx: (id) => apiClient.delete(`/finance/transactions/${id}`),
  getReceivedPayments: (params) => apiClient.get('/finance/payments', { params }),

  // Payment methods
  getPaymentMethods: () => apiClient.get('/payment-methods'),
  createPaymentMethod: (data) => apiClient.post('/payment-methods', data),
  deletePaymentMethod: (id) => apiClient.delete(`/payment-methods/${id}`),

  // Payments
  registerPaymentDetailed: (saleId, data) => apiClient.post(`/sales/${saleId}/payment-detailed`, data),
  getSalePayments: (saleId) => apiClient.get(`/sales/${saleId}/payments`),

  // Employee
  getEmployeeProfile: (userId) => apiClient.get(`/users/${userId}/employee-profile`),
  updateEmployeeProfile: (userId, data) => apiClient.put(`/users/${userId}/employee-profile`, data),

  // Bulk pricing
  applyBulkPricing: (data) => apiClient.post('/inventory/bulk-pricing', data),
  getPricingActions: (params) => apiClient.get('/inventory/pricing-actions', { params }),

  // Payroll / pagos a personal
  payPayroll: (data) => apiClient.post('/payroll/pay', data),
  getPayrollReceipt: (txId) => apiClient.get(`/payroll/${txId}/receipt`, { responseType: 'blob' }),

  // Notifications broadcast
  broadcastNotification: (data) => apiClient.post('/notifications/broadcast', data),
  getBroadcastReadStatus: (id) => apiClient.get(`/notifications/broadcast/${id}/read-status`),

  // Notifications
  getNotifications: (unreadOnly) => apiClient.get('/notifications', { params: unreadOnly ? { unread_only: true } : {} }),
  markNotificationRead: (id) => apiClient.post(`/notifications/${id}/read`),
  markAllNotificationsRead: () => apiClient.post('/notifications/read-all'),

  // Dispatch
  getDispatchQueue: (params) => apiClient.get('/dispatch/queue', { params }),

  // Over-stock notices
  getOverStockNotices: () => apiClient.get('/notices/over-stock'),
  markOverStockRead: (saleId) => apiClient.post(`/notices/over-stock/${saleId}/read`),

  // File upload
  uploadFile: (formData, purpose = 'general') => apiClient.post('/files/upload', formData, {
    params: { purpose },
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  fileUrl: (fileId) => {
    const token = authService.getToken();
    const base = process.env.REACT_APP_BACKEND_URL;
    return `${base}/api/files/${fileId}?auth=${token}`;
  },
  publicFileUrl: (fileId) => `${process.env.REACT_APP_BACKEND_URL}/api/public/files/${fileId}`,

  // Init
  initializeApp: () => apiClient.post('/init'),

  // Admin / Despliegue (master)
  getDataSummary: () => apiClient.get('/admin/data-summary'),
  downloadBackup: () => apiClient.get('/admin/backup', { responseType: 'blob' }),
  cleanupTestData: (confirm) => apiClient.post('/admin/cleanup-test-data', { confirm }),
  resetTransactional: (confirm) => apiClient.post('/admin/reset-transactional', { confirm }),

  // Compras & Finanzas
  getSuppliers: (params) => apiClient.get('/suppliers', { params }),
  createSupplier: (data) => apiClient.post('/suppliers', data),
  updateSupplier: (id, data) => apiClient.put(`/suppliers/${id}`, data),
  deleteSupplier: (id) => apiClient.delete(`/suppliers/${id}`),
  getBanks: (params) => apiClient.get('/banks', { params }),
  createBank: (data) => apiClient.post('/banks', data),
  updateBank: (id, data) => apiClient.put(`/banks/${id}`, data),
  deleteBank: (id) => apiClient.delete(`/banks/${id}`),
  getPurchases: (params) => apiClient.get('/purchases', { params }),
  createPurchase: (data) => apiClient.post('/purchases', data),
  payPurchase: (id, bankAccountId) => apiClient.post(`/purchases/${id}/pay`, { bank_account_id: bankAccountId }),
  getExpenseCategories: (params) => apiClient.get('/expense-categories', { params }),
  createExpenseCategory: (data) => apiClient.post('/expense-categories', data),
  deleteExpenseCategory: (id) => apiClient.delete(`/expense-categories/${id}`),
  getExpenses: (params) => apiClient.get('/expenses', { params }),
  createExpense: (data) => apiClient.post('/expenses', data),
  deleteExpense: (id) => apiClient.delete(`/expenses/${id}`),
  getProfitLoss: (companyId, period) => apiClient.get('/finance/profit-loss', { params: { company_id: companyId, period } }),
  getClosures: (params) => apiClient.get('/finance/closures', { params }),
  createClosure: (data) => apiClient.post('/finance/closures', data),
  reopenClosure: (id) => apiClient.post(`/finance/closures/${id}/reopen`),
  exportPurchasesReport: (companyId) => apiClient.get('/reports/purchases', { params: { company_id: companyId }, responseType: 'blob' }),
  exportExpensesReport: (companyId) => apiClient.get('/reports/expenses', { params: { company_id: companyId }, responseType: 'blob' }),
  exportPnlReport: (companyId, period) => apiClient.get('/reports/profit-loss', { params: { company_id: companyId, period }, responseType: 'blob' }),

  // Reporte PDF de marca (con logo de empresa). type: sales | finance | audit
  getBrandedReportPdf: (params) => apiClient.get('/reports/branded-pdf', { params, responseType: 'blob' }),

  // Respaldo / formateo / archivo por empresa
  companyBackup: (companyId) => apiClient.get(`/companies/${companyId}/backup`, { responseType: 'blob' }),
  companyReset: (companyId, confirm) => apiClient.post(`/companies/${companyId}/reset`, { confirm }),
  companyArchive: (companyId, confirm) => apiClient.post(`/companies/${companyId}/archive`, { confirm }),
  companyRestore: (companyId) => apiClient.post(`/companies/${companyId}/restore`),

  // Respaldos automáticos + Google Drive
  getBackupStatus: () => apiClient.get('/admin/backups/status'),
  getBackupList: () => apiClient.get('/admin/backups/list'),
  runBackupNow: () => apiClient.post('/admin/backups/run-now'),
  getDriveAuthUrl: () => apiClient.get('/admin/drive/auth-url'),
  disconnectDrive: () => apiClient.post('/admin/drive/disconnect'),

  // Tienda web (storefront)
  getStore: (slug) => apiClient.get(`/store/${slug}`),
  createStoreOrder: (slug, data) => apiClient.post(`/store/${slug}/order`, data),
  getStoreConfig: (companyId) => apiClient.get(`/companies/${companyId}/store-config`),
  updateStoreConfig: (companyId, data) => apiClient.put(`/companies/${companyId}/store-config`, data),

  // Diseño de documentos por empresa (recibos / facturas / notas)
  getDocConfig: (companyId) => apiClient.get(`/companies/${companyId}/doc-config`),
  saveDocConfig: (companyId, data) => apiClient.put(`/companies/${companyId}/doc-config`, data),

  // Planes de facturación por empresa (independientes)
  getTenantsBilling: () => apiClient.get('/tenants/billing-overview'),
  getPlanTemplates: () => apiClient.get('/plan-templates'),
  savePlanTemplates: (items) => apiClient.put('/plan-templates', { items }),
  factoryReset: (confirm, dryRun) => apiClient.post('/admin/factory-reset', { confirm, dry_run: !!dryRun }),
  testPlatformEmail: (to, template) => apiClient.post('/admin/test-email', { to, template }),
  getEmailConfig: () => apiClient.get('/admin/email-config'),
  saveEmailConfig: (cfg) => apiClient.put('/admin/email-config', cfg),

  // Email marketing / CRM
  sendMarketing: (data) => apiClient.post('/marketing/send', data),

  // SMTP por empresa (correo individual de cada empresa)
  getCompanySmtp: (companyId) => apiClient.get(`/companies/${companyId}/smtp`),
  saveCompanySmtp: (companyId, data) => apiClient.put(`/companies/${companyId}/smtp`, data),
  deleteCompanySmtp: (companyId) => apiClient.delete(`/companies/${companyId}/smtp`),
  testCompanySmtp: (companyId, to) => apiClient.post(`/companies/${companyId}/smtp/test`, { to }),

  // Historial de campañas de correo
  getCampaignHistory: (companyId) => apiClient.get('/marketing/history', { params: { company_id: companyId } }),

  // Calendario
  getCalendarEvents: (year, month, companyId) => apiClient.get('/calendar/events', { params: { year, month, ...(companyId ? { company_id: companyId } : {}) } }),
  createCalendarEvent: (data) => apiClient.post('/calendar/events', data),
  updateCalendarEvent: (id, data) => apiClient.put(`/calendar/events/${id}`, data),
  deleteCalendarEvent: (id) => apiClient.delete(`/calendar/events/${id}`),
  updateSaleCreditInfo: (saleId, data) => apiClient.patch(`/sales/${saleId}/credit-info`, data),
  registerPayment: (saleId, amount) => apiClient.post(`/sales/${saleId}/payment`, null, { params: { amount } }),
  sendCreditReminders: () => apiClient.post('/calendar/send-reminders'),

  // Celular Beach: licencia, cobranza, CRM, reportes, vitrina
  getMyLicense: () => apiClient.get('/license/me'),
  getLicense: () => apiClient.get('/license'),
  updateLicense: (data) => apiClient.put('/license', data),
  suspendLicense: (data) => apiClient.post('/license/suspend', data),
  confirmLicensePayment: (data) => apiClient.post('/license/confirm-payment', data),
  getGlobalSmtp: () => apiClient.get('/settings/global-smtp'),
  saveGlobalSmtp: (data) => apiClient.put('/settings/global-smtp', data),
  testGlobalSmtp: (to) => apiClient.post('/settings/global-smtp/test', { to }),
  getCollections: (params) => apiClient.get('/collections', { params }),
  payInstallment: (id, data) => apiClient.post(`/installments/${id}/pay`, data),
  notifyCollections: (data) => apiClient.post('/collections/notify', data),
  crmSearch: (params) => apiClient.get('/crm/clients', { params }),
  crmDetail: (id) => apiClient.get(`/crm/clients/${id}`),
  crmShareEmail: (id, to) => apiClient.post(`/crm/clients/${id}/share-email`, { to }),
  getMyCredit: () => apiClient.get('/client/credit'),
  sendClientReceipt: (data) => apiClient.post('/client/receipts', data),
  getMyReceipts: () => apiClient.get('/client/receipts'),
  scanProduct: (code, companyId) => apiClient.get('/inventory/scan', { params: { code, company_id: companyId } }),
  getReceipts: (params) => apiClient.get('/payment-receipts', { params }),
  decideReceipt: (id, data) => apiClient.patch(`/payment-receipts/${id}`, data),
  getCreditApplications: (params) => apiClient.get('/credit-applications', { params }),
  decideCreditApplication: (id, data) => apiClient.patch(`/credit-applications/${id}`, data),
  getBranchMovements: (params) => apiClient.get('/reports/branch-movements', { params }),
  downloadBranchMovements: (params) => apiClient.get('/reports/branch-movements', { params: { ...params, format: 'excel' }, responseType: 'blob' }),
  getBranchesSummary: () => apiClient.get('/branches/summary'),
  getMallConfig: () => apiClient.get('/mall-config'),
  saveMallConfig: (data) => apiClient.put('/mall-config', data),
  getPublicMall: (slug, tenant) => apiClient.get('/public/mall', { params: { ...(slug ? { slug } : {}), ...(tenant ? { tenant } : {}), domain: (typeof window !== 'undefined' ? window.location.hostname : '') } }),
  publicPrequal: (data) => apiClient.post('/public/credit-prequal', data),
  publicOrder: (data) => apiClient.post('/public/orders', data),
  publicUploadReceipt: (formData) => apiClient.post('/public/upload-receipt', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  // Fase 3
  getPolicy: () => apiClient.get('/policies/current'),
  acceptPolicy: (data) => apiClient.post('/policies/accept', data),
  updatePolicy: (text) => apiClient.put('/policies', { text }),
  getPolicyAcceptances: () => apiClient.get('/policies/acceptances'),
  resetUserPassword: (id) => apiClient.post(`/users/${id}/reset-password`),
  getErrorLogs: () => apiClient.get('/error-logs'),
  addUnits: (pid, imeis) => apiClient.post(`/products/${pid}/units`, { imeis }),
  getUnits: (pid, status) => apiClient.get(`/products/${pid}/units`, { params: status ? { status } : {} }),
  searchUnit: (imei) => apiClient.get('/units/search', { params: { imei } }),
  docLink: (kind, ref_id) => apiClient.post('/docs/link', { kind, ref_id }),
  docEmail: (kind, ref_id, to) => apiClient.post('/docs/email', { kind, ref_id, to }),
  docNotify: (kind, ref_id) => apiClient.post('/docs/notify', { kind, ref_id }),
  publicRegister: (data) => apiClient.post('/public/register', data),
  clientPrequal: (data) => apiClient.post('/client/prequal', data),
  createReservation: (data) => apiClient.post('/client/reservations', data),
  getMyReservations: () => apiClient.get('/client/reservations'),
  getReservations: (params) => apiClient.get('/reservations', { params }),
  releaseReservation: (id) => apiClient.post(`/reservations/${id}/release`),
  closeReservation: (id, data) => apiClient.post(`/reservations/${id}/close`, data),
  createTransfer: (data) => apiClient.post('/inventory/transfers', data),
  getTransfers: () => apiClient.get('/inventory/transfers'),
  settleTransfer: (id) => apiClient.post(`/inventory/transfers/${id}/settle`),
  getCreditDashboard: (params) => apiClient.get('/dashboard/credit', { params }),
  getBanners: (cid) => apiClient.get(`/branches/${cid}/banners`),
  setBanners: (cid, banners) => apiClient.put(`/branches/${cid}/banners`, { banners }),
  getVendorSettings: () => apiClient.get('/vendor/settings'),
  saveVendorSettings: (data) => apiClient.put('/vendor/settings', data),
  createVendorInvoice: (data) => apiClient.post('/vendor/invoices', data),
  getVendorInvoices: (params = {}) => apiClient.get('/vendor/invoices', { params }),
  getVendorInvoice: (id) => apiClient.get(`/vendor/invoices/${id}`),
  setVendorInvoiceStatus: (id, status) => apiClient.patch(`/vendor/invoices/${id}`, { status }),
  reportClientError: (data) => apiClient.post('/error-logs/client', data),
  // Admin SaaS extras
  clearErrorLogs: () => apiClient.delete('/error-logs'),
  clearTestErrorLogs: () => apiClient.delete('/error-logs/test'),
  // SaaS feature flags (control del Super Master)
  getSaasFeatures: () => apiClient.get('/saas/features'),
  setSaasFeatures: (patch) => apiClient.put('/saas/features', patch),
  getPublicSaasFeatures: () => apiClient.get('/public/saas/features'),
  // Memberships (clientes multi-empresa)
  getMyMemberships: () => apiClient.get('/client/memberships'),
  // Promociones / Marketplace
  listPromotions: (params = {}) => apiClient.get('/promotions', { params }),
  createPromotion: (data) => apiClient.post('/promotions', data),
  submitPromotion: (pid) => apiClient.patch(`/promotions/${pid}/submit`),
  approvePromotion: (pid) => apiClient.patch(`/promotions/${pid}/approve`),
  rejectPromotion: (pid, reason) => apiClient.patch(`/promotions/${pid}/reject`, { reason }),
  hidePromotion: (pid) => apiClient.patch(`/promotions/${pid}/hide`),
  featurePromotion: (pid, featured) => apiClient.patch(`/promotions/${pid}/feature`, { featured }),
  deletePromotion: (pid) => apiClient.delete(`/promotions/${pid}`),
  getPublicMarketplace: (params = {}) => apiClient.get('/public/marketplace', { params }),
  getClientMarketplace: (params = {}) => apiClient.get('/client/marketplace', { params }),
  // Prospectos
  prospectSignup: (data) => apiClient.post('/public/prospect/signup', data),
  requestAffiliation: (tenant_id, message) => apiClient.post('/client/affiliation-request', { tenant_id, message }),
  listAffiliationRequests: () => apiClient.get('/tenant/affiliation-requests'),
  // Rubros (verticals)
  getVerticals: () => apiClient.get('/verticals'),
  setTenantVertical: (tid, vertical) => apiClient.patch(`/tenants/${tid}/vertical`, { vertical }),
  // Right Hand (colaboradores Master)
  getRightHandPermsCatalog: () => apiClient.get('/right-hand/perms-catalog'),
  listRightHand: () => apiClient.get('/right-hand'),
  createRightHand: (data) => apiClient.post('/right-hand', data),
  updateRightHand: (uid, data) => apiClient.patch(`/right-hand/${uid}`, data),
  deleteRightHand: (uid) => apiClient.delete(`/right-hand/${uid}`),
};

export default apiClient;